/**
 * Track B card rows (INC-1007, UI half): the strict decoder of one `trackb_recommendations` row.
 *
 * Mirrors `ssi.api.views_trackb`. An extra key, a missing key (a missing as-of included), a
 * JSON number where a canonical decimal string belongs, or a card that contradicts itself is
 * refused. A rank is never a probability: the probability exists exactly when the card is
 * calibrated.
 */
import {
  ContractViolation,
  exact,
  field,
  instant,
  int,
  listOf,
  nat,
  nullable,
  oneOf,
  str,
  type Reader,
} from "../../contracts/decode.ts";
import { day } from "../../contracts/ops.ts";
import { parseInstant } from "../../app/format.ts";

/** The projection this view reads (`constants_trackb.json`). */
export const TRACKB_PROJECTION = "trackb_recommendations";
export const TRACKB_HORIZONS = ["VERY_SHORT", "SHORT", "MEDIUM", "LONG", "VERY_LONG"] as const;
export const TRACKB_CALIBRATIONS = ["UNCALIBRATED", "CALIBRATED"] as const;
export const TRACKB_SECTIONS = [
  "universe",
  "factors",
  "sentiment",
  "macro",
  "regime",
  "upside",
] as const;

export type Horizon = (typeof TRACKB_HORIZONS)[number];

const HEX64 = /^[0-9a-f]{64}$/;
const ISIN = /^[A-Z]{2}[A-Z0-9]{9}[0-9]$/;
const SLUG = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const DECIMAL = /^(-?)(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d{1,3}))?$/;
/**
 * The server's canonical decimal text (`ssi.contracts._canon.decimal_text`): positional, no
 * trailing zero, no leading zero, no exponent, no sign on zero. The manifest hash covers this
 * text, so any other spelling of the same number is a card altered on the way (review F2).
 */
const CANONICAL = /^(?:0|-?[1-9]\d*(?:\.\d*[1-9])?|-?0\.\d*[1-9])$/;
/** India Standard Time is UTC+05:30 all year (no daylight saving), in microseconds. */
const IST_OFFSET_US = 19_800_000_000n;
const US_PER_DAY = 86_400_000_000n;

function ensure(condition: boolean, path: string, expected: string): void {
  if (!condition) {
    throw new ContractViolation(path, expected);
  }
}

function patterned(pattern: RegExp, what: string): Reader<string> {
  return (value, path) => {
    const text = str(value, path);
    ensure(pattern.test(text), path, what);
    return text;
  };
}

const hex64 = patterned(HEX64, "a SHA-256 hex digest");
const isin = patterned(ISIN, "an ISIN");
const slug = patterned(SLUG, "a slug");
const decimalText = patterned(CANONICAL, "a canonical decimal string (never a JSON number)");
const positive: Reader<number> = (value, path) => {
  const found = nat(value, path);
  ensure(found >= 1, path, "a positive integer");
  return found;
};

/** A decimal string as an integer and a power of ten, so comparisons are exact (no floats). */
function scaled(text: string): { readonly n: bigint; readonly scale: number } {
  // Callers pass text that `decimalText` already matched, so the match exists.
  const [, sign, whole, fraction = "", exponent = "0"] = DECIMAL.exec(text) as unknown as [
    string,
    string,
    string,
    string | undefined,
    string | undefined,
  ];
  const n = BigInt(`${whole}${fraction}`);
  return { n: sign === "-" ? -n : n, scale: fraction.length - Number(exponent) };
}

/** The exchange (IST) calendar date of a UTC instant, by integer arithmetic. */
export function istDay(text: string): string {
  const days = (parseInstant(text) + IST_OFFSET_US) / US_PER_DAY;
  const date = new Date(Number(days) * 86_400_000);
  const two = (n: number): string => String(n).padStart(2, "0");
  return `${String(date.getUTCFullYear())}-${two(date.getUTCMonth() + 1)}-${two(date.getUTCDate())}`;
}

/** Compare two decimal strings exactly: negative, zero or positive. */
export function compareDecimal(left: string, right: string): number {
  const a = scaled(left);
  const b = scaled(right);
  const scale = Math.max(a.scale, b.scale);
  const x = a.n * 10n ** BigInt(scale - a.scale);
  const y = b.n * 10n ** BigInt(scale - b.scale);
  return x < y ? -1 : x > y ? 1 : 0;
}

const nonNegative: Reader<string> = (value, path) => {
  const found = decimalText(value, path);
  ensure(compareDecimal(found, "0") >= 0, path, "a non-negative decimal");
  return found;
};

function within(low: string, high: string, what: string): Reader<string> {
  return (value, path) => {
    const found = decimalText(value, path);
    ensure(compareDecimal(found, low) >= 0 && compareDecimal(found, high) <= 0, path, what);
    return found;
  };
}

export interface Source {
  readonly role: string;
  readonly manifestHash: string;
}

const readSource: Reader<Source> = (value, path) => {
  const found = exact(value, path, ["manifest_hash", "role"]);
  return {
    role: field(found, path, "role", slug),
    manifestHash: field(found, path, "manifest_hash", hex64),
  };
};

function sources(required: boolean): Reader<Source[]> {
  return (value, path) => {
    const found = listOf(readSource)(value, path);
    ensure(!required || found.length > 0, path, "at least one source (a number has a source)");
    return found;
  };
}

export interface Universe {
  readonly asOf: string;
  readonly universeHash: string;
  readonly size: number;
  readonly listedFrom: string;
  readonly listingAvailableAt: string;
  readonly version: string;
  readonly sources: readonly Source[];
}

const readUniverse: Reader<Universe> = (value, path) => {
  const found = exact(value, path, [
    "as_of",
    "listed_from",
    "listing_available_at",
    "size",
    "sources",
    "universe_hash",
    "version",
  ]);
  return {
    asOf: field(found, path, "as_of", instant),
    universeHash: field(found, path, "universe_hash", hex64),
    size: field(found, path, "size", positive),
    listedFrom: field(found, path, "listed_from", day),
    listingAvailableAt: field(found, path, "listing_available_at", instant),
    version: field(found, path, "version", slug),
    sources: field(found, path, "sources", sources(true)),
  };
};

export interface Contribution {
  readonly feature: string;
  readonly weight: string;
  readonly raw: string | null;
  readonly zscore: string | null;
  readonly rank: number | null;
  readonly contribution: string | null;
  readonly asOf: string;
  readonly sources: readonly Source[];
}

const readContribution: Reader<Contribution> = (value, path) => {
  const found = exact(value, path, [
    "as_of",
    "contribution",
    "feature",
    "rank",
    "raw",
    "sources",
    "weight",
    "zscore",
  ]);
  const out: Contribution = {
    feature: field(found, path, "feature", slug),
    weight: field(found, path, "weight", within("0", "1", "a weight in [0, 1]")),
    raw: field(found, path, "raw", nullable(decimalText)),
    zscore: field(found, path, "zscore", nullable(decimalText)),
    rank: field(found, path, "rank", nullable(positive)),
    contribution: field(found, path, "contribution", nullable(decimalText)),
    asOf: field(found, path, "as_of", instant),
    sources: field(found, path, "sources", sources(true)),
  };
  ensure((out.rank === null) === (out.raw === null), path, "a rank exactly when a raw value");
  return out;
};

export interface Factors {
  readonly asOf: string;
  readonly score: string | null;
  readonly rank: number | null;
  readonly contributions: readonly Contribution[];
  readonly matrixHash: string;
  readonly weightsHash: string;
  readonly version: string;
  readonly sources: readonly Source[];
}

const readFactors: Reader<Factors> = (value, path) => {
  const found = exact(value, path, [
    "as_of",
    "contributions",
    "matrix_hash",
    "rank",
    "score",
    "sources",
    "version",
    "weights_hash",
  ]);
  const out: Factors = {
    asOf: field(found, path, "as_of", instant),
    score: field(found, path, "score", nullable(decimalText)),
    rank: field(found, path, "rank", nullable(positive)),
    contributions: field(found, path, "contributions", listOf(readContribution)),
    matrixHash: field(found, path, "matrix_hash", hex64),
    weightsHash: field(found, path, "weights_hash", hex64),
    version: field(found, path, "version", slug),
    sources: field(found, path, "sources", sources(true)),
  };
  ensure((out.rank === null) === (out.score === null), path, "a rank exactly when a score");
  ensure(out.contributions.length > 0, path, "a score has contributions");
  return out;
};

export interface Sentiment {
  readonly asOf: string;
  readonly value: string | null;
  readonly weight: string;
  readonly replies: readonly string[];
  readonly promptHash: string;
  readonly modelId: string;
  readonly decaySessions: number;
  readonly formula: string;
  readonly availableAt: string | null;
  readonly sources: readonly Source[];
}

const readSentiment: Reader<Sentiment> = (value, path) => {
  const found = exact(value, path, [
    "as_of",
    "available_at",
    "decay_sessions",
    "formula",
    "model_id",
    "prompt_hash",
    "replies",
    "sources",
    "value",
    "weight",
  ]);
  const out: Sentiment = {
    asOf: field(found, path, "as_of", instant),
    value: field(found, path, "value", nullable(within("-1", "1", "a sentiment in [-1, 1]"))),
    weight: field(found, path, "weight", nonNegative),
    replies: field(found, path, "replies", listOf(hex64)),
    promptHash: field(found, path, "prompt_hash", hex64),
    modelId: field(found, path, "model_id", slug),
    decaySessions: field(found, path, "decay_sessions", positive),
    formula: field(found, path, "formula", patterned(/^\S.*$/, "a formula name")),
    availableAt: field(found, path, "available_at", nullable(instant)),
    sources: field(found, path, "sources", sources(false)),
  };
  ensure(
    out.value === null ||
      (out.replies.length > 0 && out.availableAt !== null && out.sources.length > 0),
    path,
    "a sentiment value names its replies, their availability and its sources",
  );
  return out;
};

/** A fitted beta with its whole window, or only the reason there is none. */
export type Beta =
  | {
      readonly kind: "fit";
      readonly factor: string;
      readonly stage: number;
      readonly beta: string;
      readonly standardError: string;
      readonly ridgeLambda: string;
      readonly observations: number;
      readonly firstSession: string;
      readonly lastSession: string;
      readonly unit: string;
    }
  | { readonly kind: "none"; readonly factor: string; readonly noBeta: string };

const readBeta: Reader<Beta> = (value, path) => {
  const found = exact(value, path, [
    "beta",
    "factor",
    "first_session",
    "last_session",
    "no_beta",
    "observations",
    "ridge_lambda",
    "stage",
    "standard_error",
    "unit",
  ]);
  const factor = field(found, path, "factor", slug);
  const noBeta = field(found, path, "no_beta", nullable(slug));
  const stage = field(found, path, "stage", nullable(positive));
  const beta = field(found, path, "beta", nullable(decimalText));
  const standardError = field(found, path, "standard_error", nullable(decimalText));
  const ridgeLambda = field(found, path, "ridge_lambda", nullable(decimalText));
  const observations = field(found, path, "observations", nullable(positive));
  const firstSession = field(found, path, "first_session", nullable(day));
  const lastSession = field(found, path, "last_session", nullable(day));
  const unit = field(found, path, "unit", nullable(slug));
  const facts = [
    stage,
    beta,
    standardError,
    ridgeLambda,
    observations,
    firstSession,
    lastSession,
    unit,
  ];
  if (noBeta !== null && facts.every((fact) => fact === null)) {
    return { kind: "none", factor, noBeta };
  }
  if (
    noBeta === null &&
    stage !== null &&
    beta !== null &&
    standardError !== null &&
    ridgeLambda !== null &&
    observations !== null &&
    firstSession !== null &&
    lastSession !== null &&
    unit !== null
  ) {
    return {
      kind: "fit",
      factor,
      stage,
      beta,
      standardError,
      ridgeLambda,
      observations,
      firstSession,
      lastSession,
      unit,
    };
  }
  throw new ContractViolation(path, "a beta with its window, lambda and error, or a reason alone");
};

export interface Macro {
  readonly asOf: string;
  readonly sectorIndex: string | null;
  readonly windowSessions: number;
  readonly observations: number | null;
  readonly betas: readonly Beta[];
  readonly exposureHash: string | null;
  readonly registrationHash: string;
  readonly unavailable: string | null;
  readonly version: string;
  readonly sources: readonly Source[];
}

const readMacro: Reader<Macro> = (value, path) => {
  const found = exact(value, path, [
    "as_of",
    "betas",
    "exposure_hash",
    "observations",
    "registration_hash",
    "sector_index",
    "sources",
    "unavailable",
    "version",
    "window_sessions",
  ]);
  const out: Macro = {
    asOf: field(found, path, "as_of", instant),
    sectorIndex: field(found, path, "sector_index", nullable(slug)),
    windowSessions: field(found, path, "window_sessions", positive),
    observations: field(found, path, "observations", nullable(nat)),
    betas: field(found, path, "betas", listOf(readBeta)),
    exposureHash: field(found, path, "exposure_hash", nullable(hex64)),
    registrationHash: field(found, path, "registration_hash", hex64),
    unavailable: field(found, path, "unavailable", nullable(slug)),
    version: field(found, path, "version", slug),
    sources: field(found, path, "sources", sources(true)),
  };
  const fit = [out.sectorIndex, out.observations, out.exposureHash];
  const fitted = fit.every((fact) => fact !== null) && out.betas.length > 0;
  const missing =
    fit.every((fact) => fact === null) && out.betas.length === 0 && out.unavailable !== null;
  ensure(
    (fitted && out.unavailable === null) || missing,
    path,
    "a fit with its sector, observations, betas and hash, or a missing fit with its reason",
  );
  return out;
};

export interface Component {
  readonly component: string;
  readonly raw: string | null;
  readonly score: string | null;
  readonly weight: string;
}

const readComponent: Reader<Component> = (value, path) => {
  const found = exact(value, path, ["component", "raw", "score", "weight"]);
  return {
    component: field(found, path, "component", slug),
    raw: field(found, path, "raw", nullable(decimalText)),
    score: field(found, path, "score", nullable(within("0", "100", "a score in [0, 100]"))),
    weight: field(found, path, "weight", decimalText),
  };
};

export interface Regime {
  readonly asOf: string;
  readonly session: string;
  readonly composite: string | null;
  readonly label: string | null;
  readonly components: readonly Component[];
  readonly readingHash: string;
  readonly recipeHash: string;
  readonly sessionRule: string;
  readonly version: string;
  readonly sources: readonly Source[];
}

const readRegime: Reader<Regime> = (value, path) => {
  const found = exact(value, path, [
    "as_of",
    "components",
    "composite",
    "label",
    "reading_hash",
    "recipe_hash",
    "session",
    "session_rule",
    "sources",
    "version",
  ]);
  const out: Regime = {
    asOf: field(found, path, "as_of", instant),
    session: field(found, path, "session", day),
    composite: field(
      found,
      path,
      "composite",
      nullable(within("0", "100", "a composite in [0, 100]")),
    ),
    label: field(found, path, "label", nullable(slug)),
    components: field(found, path, "components", listOf(readComponent)),
    readingHash: field(found, path, "reading_hash", hex64),
    recipeHash: field(found, path, "recipe_hash", hex64),
    sessionRule: field(found, path, "session_rule", slug),
    version: field(found, path, "version", slug),
    sources: field(found, path, "sources", sources(true)),
  };
  ensure(
    (out.label === null) === (out.composite === null),
    path,
    "a label exactly with a composite",
  );
  return out;
};

export interface Upside {
  readonly asOf: string;
  readonly period: string;
  readonly dispersion: string | null;
  readonly estimates: number;
  readonly windowSessions: number;
  readonly revisionsUp: number | null;
  readonly revisionsDown: number | null;
  readonly revisionsNet: number | null;
  readonly resultsDays: number | null;
  readonly resultsDate: string | null;
  readonly corporateDays: number | null;
  readonly corporateDate: string | null;
  readonly availableAt: string | null;
  readonly version: string;
  readonly sources: readonly Source[];
}

const readUpside: Reader<Upside> = (value, path) => {
  const found = exact(value, path, [
    "as_of",
    "available_at",
    "corporate_date",
    "corporate_days",
    "dispersion",
    "estimates",
    "period",
    "results_date",
    "results_days",
    "revisions_down",
    "revisions_net",
    "revisions_up",
    "sources",
    "version",
    "window_sessions",
  ]);
  const out: Upside = {
    asOf: field(found, path, "as_of", instant),
    period: field(found, path, "period", patterned(/^\S.*$/, "a period")),
    dispersion: field(found, path, "dispersion", nullable(nonNegative)),
    estimates: field(found, path, "estimates", nat),
    windowSessions: field(found, path, "window_sessions", positive),
    revisionsUp: field(found, path, "revisions_up", nullable(nat)),
    revisionsDown: field(found, path, "revisions_down", nullable(nat)),
    revisionsNet: field(found, path, "revisions_net", nullable(int)),
    resultsDays: field(found, path, "results_days", nullable(nat)),
    resultsDate: field(found, path, "results_date", nullable(day)),
    corporateDays: field(found, path, "corporate_days", nullable(nat)),
    corporateDate: field(found, path, "corporate_date", nullable(day)),
    availableAt: field(found, path, "available_at", nullable(instant)),
    version: field(found, path, "version", slug),
    sources: field(found, path, "sources", sources(true)),
  };
  const counts = [out.revisionsUp, out.revisionsDown, out.revisionsNet];
  ensure(
    counts.every((count) => count === null) ||
      (out.revisionsUp !== null &&
        out.revisionsDown !== null &&
        out.revisionsNet === out.revisionsUp - out.revisionsDown),
    path,
    "net revisions are up less down",
  );
  ensure(
    (out.resultsDays === null) === (out.resultsDate === null),
    path,
    "results days with a date",
  );
  ensure(
    (out.corporateDays === null) === (out.corporateDate === null),
    path,
    "corporate days with a date",
  );
  return out;
};

export interface TrackbRow {
  readonly version: string;
  readonly entityId: string;
  readonly isin: string;
  readonly horizon: Horizon;
  readonly asOf: string;
  readonly calibration: (typeof TRACKB_CALIBRATIONS)[number];
  readonly probability: string | null;
  readonly universe: Universe;
  readonly factors: Factors;
  readonly sentiment: Sentiment;
  readonly macro: Macro;
  readonly regime: Regime;
  readonly upside: Upside;
  readonly manifestHash: string;
}

/** Decode one Track B card; throws `ContractViolation` on any deviation. */
export function decodeTrackbRow(value: unknown, path = "row"): TrackbRow {
  const found = exact(value, path, [
    "as_of",
    "calibration",
    "entity_id",
    "factors",
    "horizon",
    "isin",
    "macro",
    "manifest_hash",
    "probability",
    "regime",
    "sentiment",
    "universe",
    "upside",
    "version",
  ]);
  const row: TrackbRow = {
    version: field(found, path, "version", slug),
    entityId: field(found, path, "entity_id", str),
    isin: field(found, path, "isin", isin),
    horizon: field(found, path, "horizon", oneOf(TRACKB_HORIZONS)),
    asOf: field(found, path, "as_of", instant),
    calibration: field(found, path, "calibration", oneOf(TRACKB_CALIBRATIONS)),
    probability: field(
      found,
      path,
      "probability",
      nullable(within("0", "1", "a probability in [0, 1]")),
    ),
    universe: field(found, path, "universe", readUniverse),
    factors: field(found, path, "factors", readFactors),
    sentiment: field(found, path, "sentiment", readSentiment),
    macro: field(found, path, "macro", readMacro),
    regime: field(found, path, "regime", readRegime),
    upside: field(found, path, "upside", readUpside),
    manifestHash: field(found, path, "manifest_hash", hex64),
  };
  ensure(
    (row.calibration === "CALIBRATED") === (row.probability !== null),
    `${path}.probability`,
    "a probability exactly when calibrated (a rank is not a probability)",
  );
  for (const name of TRACKB_SECTIONS) {
    ensure(row[name].asOf === row.asOf, `${path}.${name}.as_of`, "the card's as-of");
  }
  for (const [index, line] of row.factors.contributions.entries()) {
    ensure(
      line.asOf === row.asOf,
      `${path}.factors.contributions[${String(index)}].as_of`,
      "the card's as-of",
    );
  }
  // Nothing the card shows was known after its as-of (`TrackbRecommendationRow._visible`).
  const known = [
    row.universe.listingAvailableAt,
    row.sentiment.availableAt,
    row.upside.availableAt,
  ].every((when) => when === null || parseInstant(when) <= parseInstant(row.asOf));
  const today = istDay(row.asOf);
  const dated = [
    row.universe.listedFrom,
    row.regime.session,
    ...row.macro.betas.map((beta) => (beta.kind === "fit" ? beta.lastSession : null)),
  ].every((each) => each === null || each <= today);
  ensure(known && dated, path, "nothing shown is known after the as-of (look-ahead)");
  const stamp = row.asOf.replace(/[-:]/g, "").replace(/\.\d{6}Z$/, "Z");
  ensure(
    row.entityId === `${row.isin}.${row.horizon}.${stamp}`,
    `${path}.entity_id`,
    "<isin>.<horizon>.<as-of>",
  );
  return row;
}
